import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './contexts/LanguageContext';
import { SiteProvider, useSite } from './contexts/SiteContext';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import { DynamicHeader } from './components/DynamicHeader';
import { DynamicFooter } from './components/DynamicFooter';
import { WhatsAppWidget } from './components/WhatsAppWidget';
import { SitePreviewBar } from './components/SitePreviewBar';
import { Toaster } from './components/ui/sonner';
import ChatWidget from './components/ChatWidget';
import RoutePrefetcher from './components/RoutePrefetcher';

// Eager home pages (route "/") - kept in main bundle to protect LCP
import { DynamicHome } from './pages/DynamicHome';
import { MainHubHome } from './pages/MainHubHome';

// Helper for lazy-loading named exports
const named = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));

// Lazy-loaded common pages (code-split into separate chunks)
const About = named(() => import('./pages/About'), 'About');
const Publications = lazy(() => import('./pages/Publications'));
const InTheNews = lazy(() => import('./pages/InTheNews'));
const Conferences = lazy(() => import('./pages/Conferences'));
const MediaKit = lazy(() => import('./pages/MediaKit'));
const AwardsMemberships = lazy(() => import('./pages/AwardsMemberships'));
const Contact = named(() => import('./pages/Contact'), 'Contact');
const Blog = named(() => import('./pages/Blog'), 'Blog');
const BlogArticle = named(() => import('./pages/BlogArticle'), 'BlogArticle');
const Resources = named(() => import('./pages/Resources'), 'Resources');
const ConditionPage = named(() => import('./pages/ConditionPage'), 'ConditionPage');
const TestimonialsPage = named(() => import('./pages/TestimonialsPage'), 'TestimonialsPage');
const Team = named(() => import('./pages/Team'), 'Team');
const FAQPage = lazy(() => import('./pages/FAQPage'));
const InternationalPatientsPage = lazy(() => import('./pages/InternationalPatientsPage'));
const InternationalFellowshipsPage = lazy(() => import('./pages/InternationalFellowshipsPage'));

// Admin Pages (lazy - behind auth, never part of public LCP)
const AdminLogin = named(() => import('./pages/admin'), 'AdminLogin');
const AdminLayout = named(() => import('./pages/admin'), 'AdminLayout');
const AdminDashboard = named(() => import('./pages/admin'), 'AdminDashboard');
const AdminSettings = named(() => import('./pages/admin'), 'AdminSettings');
const AdminSubscribers = named(() => import('./pages/admin'), 'AdminSubscribers');
const AdminGroups = named(() => import('./pages/admin'), 'AdminGroups');
const AdminSendMessage = named(() => import('./pages/admin'), 'AdminSendMessage');
const AdminLogs = named(() => import('./pages/admin'), 'AdminLogs');
const AdminContent = named(() => import('./pages/admin'), 'AdminContent');
const AdminCalendar = named(() => import('./pages/admin'), 'AdminCalendar');
const AdminSync = named(() => import('./pages/admin'), 'AdminSync');
const AdminBulkBroadcast = named(() => import('./pages/admin'), 'AdminBulkBroadcast');

// SEO Landing Pages (lazy - indexed by search engines, code-split)
const ACLReconstruction = lazy(() => import('./pages/seo/ACLReconstruction'));
const KneeReplacement = lazy(() => import('./pages/seo/KneeReplacement'));
const HipReplacement = lazy(() => import('./pages/seo/HipReplacement'));
const KneeArthroscopy = lazy(() => import('./pages/seo/KneeArthroscopy'));
const SportsInjuries = lazy(() => import('./pages/seo/SportsInjuries'));
const MeniscalTears = lazy(() => import('./pages/seo/MeniscalTears'));
const RoboticSurgery = lazy(() => import('./pages/seo/RoboticSurgery'));
const Trauma = lazy(() => import('./pages/seo/Trauma'));
const TennisElbow = lazy(() => import('./pages/seo/TennisElbow'));
const GolfersElbow = lazy(() => import('./pages/seo/GolfersElbow'));
const ElbowPainTreatment = lazy(() => import('./pages/seo/ElbowPainTreatment'));
const FrozenShoulder = lazy(() => import('./pages/seo/FrozenShoulder'));
const RotatorCuffTear = lazy(() => import('./pages/seo/RotatorCuffTear'));
const ShoulderDislocation = lazy(() => import('./pages/seo/ShoulderDislocation'));
const CarpalTunnel = lazy(() => import('./pages/seo/CarpalTunnel'));
const TriggerFinger = lazy(() => import('./pages/seo/TriggerFinger'));
const ShoulderArthroscopy = lazy(() => import('./pages/seo/ShoulderArthroscopy'));
const ShoulderReplacement = lazy(() => import('./pages/seo/ShoulderReplacement'));
const ShoulderArthritis = lazy(() => import('./pages/seo/ShoulderArthritis'));
const KneeArthritis = lazy(() => import('./pages/seo/KneeArthritis'));
const HipArthritis = lazy(() => import('./pages/seo/HipArthritis'));
const CartilageRepair = lazy(() => import('./pages/seo/CartilageRepair'));
const AvascularNecrosis = lazy(() => import('./pages/seo/AvascularNecrosis'));
const AnkleInjuries = lazy(() => import('./pages/seo/AnkleInjuries'));
const JointReplacement = lazy(() => import('./pages/seo/JointReplacement'));
const DAAHipReplacement = lazy(() => import('./pages/seo/DAAHipReplacement'));
const PrecisionSurgery = lazy(() => import('./pages/seo/PrecisionSurgery'));
const OrthoSurgeonHyderabad = lazy(() => import('./pages/seo/OrthoSurgeonHyderabad'));
const JointReplacementCost = lazy(() => import('./pages/seo/JointReplacementCost'));

// NEW SEO Blog Articles (lazy)
const KneeReplacementRecovery = lazy(() => import('./pages/seo/KneeReplacementRecovery'));
const FrozenShoulderExercises = lazy(() => import('./pages/seo/FrozenShoulderExercises'));
const ShoulderPainAtNight = lazy(() => import('./pages/seo/ShoulderPainAtNight'));
const MedicalTourismJointSurgery = lazy(() => import('./pages/seo/MedicalTourismJointSurgery'));
const PhysiotherapyAfterKneeSurgery = lazy(() => import('./pages/seo/PhysiotherapyAfterKneeSurgery'));
const HipReplacementRecovery = lazy(() => import('./pages/seo/HipReplacementRecovery'));
const RotatorCuffSurgeryCost = lazy(() => import('./pages/seo/RotatorCuffSurgeryCost'));

import './App.css';

// Suspense fallback shown while a route chunk loads
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Protected Route for Admin
const ProtectedAdminRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdminAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Inner app with routing (needs access to site context)
const AppContent = () => {
  const { isMainSite, conditions } = useSite();

  return (
    <div className="App min-h-screen flex flex-col">
      <ScrollToTop />
      <RoutePrefetcher />
      <SitePreviewBar />
      <DynamicHeader />
      <main className="flex-grow">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Home page - different for main hub vs specialty sites */}
          <Route path="/" element={isMainSite() ? <MainHubHome /> : <DynamicHome />} />
          
          {/* Common pages */}
          <Route path="/about" element={<About />} />
          <Route path="/publications" element={<Publications />} />
          <Route path="/research" element={<Publications />} />
          <Route path="/in-the-news" element={<InTheNews />} />
          <Route path="/media" element={<InTheNews />} />
          <Route path="/conferences" element={<Conferences />} />
          <Route path="/talks" element={<Conferences />} />
          <Route path="/media-kit" element={<MediaKit />} />
          <Route path="/awards" element={<AwardsMemberships />} />
          <Route path="/awards-memberships" element={<AwardsMemberships />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/testimonials" element={<TestimonialsPage />} />
          <Route path="/reviews" element={<TestimonialsPage />} />
          <Route path="/patient-reviews" element={<TestimonialsPage />} />
          <Route path="/team" element={<Team />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/faqs" element={<FAQPage />} />
          <Route path="/international-patients" element={<InternationalPatientsPage />} />
          <Route path="/medical-tourism" element={<InternationalPatientsPage />} />
          <Route path="/fellowships" element={<InternationalFellowshipsPage />} />
          <Route path="/international-training" element={<InternationalFellowshipsPage />} />
          <Route path="/credentials" element={<InternationalFellowshipsPage />} />
          
          {/* SEO Landing Pages - Hidden from navigation, indexed by search engines */}
          <Route path="/precision-surgery" element={<PrecisionSurgery />} />
          <Route path="/computer-navigation" element={<PrecisionSurgery />} />
          <Route path="/acl-reconstruction" element={<ACLReconstruction />} />
          <Route path="/knee-replacement" element={<KneeReplacement />} />
          <Route path="/hip-replacement" element={<HipReplacement />} />
          <Route path="/knee-arthroscopy" element={<KneeArthroscopy />} />
          <Route path="/sports-injuries" element={<SportsInjuries />} />
          <Route path="/meniscal-tears" element={<MeniscalTears />} />
          <Route path="/robotic-surgery" element={<RoboticSurgery />} />
          <Route path="/trauma" element={<Trauma />} />
          <Route path="/tennis-elbow" element={<TennisElbow />} />
          <Route path="/golfers-elbow" element={<GolfersElbow />} />
          <Route path="/elbow-pain-treatment-hyderabad" element={<ElbowPainTreatment />} />
          <Route path="/elbow-pain-hyderabad" element={<ElbowPainTreatment />} />
          <Route path="/elbow-pain-treatment" element={<ElbowPainTreatment />} />
          <Route path="/frozen-shoulder" element={<FrozenShoulder />} />
          <Route path="/rotator-cuff-tear" element={<RotatorCuffTear />} />
          <Route path="/shoulder-dislocation" element={<ShoulderDislocation />} />
          <Route path="/carpal-tunnel" element={<CarpalTunnel />} />
          <Route path="/trigger-finger" element={<TriggerFinger />} />
          <Route path="/shoulder-arthroscopy" element={<ShoulderArthroscopy />} />
          <Route path="/shoulder-replacement" element={<ShoulderReplacement />} />
          <Route path="/shoulder-arthritis" element={<ShoulderArthritis />} />
          <Route path="/knee-arthritis" element={<KneeArthritis />} />
          <Route path="/hip-arthritis" element={<HipArthritis />} />
          <Route path="/cartilage-repair" element={<CartilageRepair />} />
          <Route path="/avascular-necrosis" element={<AvascularNecrosis />} />
          <Route path="/ankle-injuries" element={<AnkleInjuries />} />
          <Route path="/joint-replacement" element={<JointReplacement />} />
          <Route path="/daa-hip-replacement" element={<DAAHipReplacement />} />
          <Route path="/daa-minimally-invasive-anterior-approach-hip-replacement" element={<DAAHipReplacement />} />
          
          {/* NEW: High-Value Local SEO Pages */}
          <Route path="/orthopedic-surgeon-hyderabad" element={<OrthoSurgeonHyderabad />} />
          <Route path="/best-ortho-doctor-hyderabad" element={<OrthoSurgeonHyderabad />} />
          <Route path="/ortho-specialist-hyderabad" element={<OrthoSurgeonHyderabad />} />
          <Route path="/joint-replacement-cost-hyderabad" element={<JointReplacementCost />} />
          <Route path="/knee-replacement-cost-hyderabad" element={<JointReplacementCost />} />
          <Route path="/hip-replacement-cost-hyderabad" element={<JointReplacementCost />} />
          <Route path="/surgery-cost-india" element={<JointReplacementCost />} />
          
          {/* NEW: SEO Blog Articles (High-Value Keywords) */}
          <Route path="/knee-replacement-recovery-timeline" element={<KneeReplacementRecovery />} />
          <Route path="/knee-replacement-recovery" element={<KneeReplacementRecovery />} />
          <Route path="/recovery-after-knee-replacement" element={<KneeReplacementRecovery />} />
          <Route path="/exercises-for-frozen-shoulder" element={<FrozenShoulderExercises />} />
          <Route path="/frozen-shoulder-exercises" element={<FrozenShoulderExercises />} />
          <Route path="/shoulder-pain-at-night" element={<ShoulderPainAtNight />} />
          <Route path="/night-shoulder-pain" element={<ShoulderPainAtNight />} />
          <Route path="/medical-tourism-joint-surgery-india" element={<MedicalTourismJointSurgery />} />
          <Route path="/joint-surgery-india-medical-tourism" element={<MedicalTourismJointSurgery />} />
          <Route path="/physiotherapy-after-knee-surgery" element={<PhysiotherapyAfterKneeSurgery />} />
          <Route path="/knee-replacement-physiotherapy" element={<PhysiotherapyAfterKneeSurgery />} />
          <Route path="/hip-replacement-recovery" element={<HipReplacementRecovery />} />
          <Route path="/hip-replacement-recovery-timeline" element={<HipReplacementRecovery />} />
          <Route path="/recovery-after-hip-replacement" element={<HipReplacementRecovery />} />
          <Route path="/rotator-cuff-surgery-cost-india" element={<RotatorCuffSurgeryCost />} />
          <Route path="/rotator-cuff-repair-cost" element={<RotatorCuffSurgeryCost />} />
          
          {/* SEO URL Aliases - Alternate URLs indexed by Google */}
          <Route path="/knee-joint-replacement-surgery-in-hyderabad" element={<KneeReplacement />} />
          <Route path="/knee-joint-replacement-surgery-dr-deepthi-nandan-reddy" element={<KneeReplacement />} />
          <Route path="/best-orthopedic-surgeon-in-hyderabad" element={<OrthoSurgeonHyderabad />} />
          <Route path="/best-orthopedic-surgeon-in-hyderabad/" element={<OrthoSurgeonHyderabad />} />
          <Route path="/rotator-cuff-repair-surgery-in-hyderabad" element={<RotatorCuffTear />} />
          <Route path="/shoulder-reconstruction-hyderabad" element={<ShoulderArthroscopy />} />
          <Route path="/shoulder-replacement-surgery-in-hyderabad" element={<ShoulderReplacement />} />
          <Route path="/elbow-replacement-hyderabad" element={<TennisElbow />} />
          <Route path="/hand-fractures-treatment-in-hyderabad" element={<CarpalTunnel />} />
          <Route path="/non-surgical-shoulder-treatment-in-hyderabad" element={<FrozenShoulder />} />
          <Route path="/lower-limb-orthopedic-specialist-hyderabad" element={<JointReplacement />} />
          <Route path="/hip-joint-replacement-surgery-in-hyderabad" element={<HipReplacement />} />
          <Route path="/total-hip-replacement-hyderabad" element={<HipReplacement />} />
          <Route path="/total-knee-replacement-hyderabad" element={<KneeReplacement />} />
          <Route path="/orthopedic-doctor-hyderabad" element={<MainHubHome />} />
          <Route path="/orthopedic-surgeon-near-me" element={<MainHubHome />} />
          
          {/* Legacy URLs from Google Search Console - Jan 2026 */}
          {/* These URLs were crawled by Google and need proper handling */}
          <Route path="/elbow-fracture-surgery-in-hyderabad" element={<GolfersElbow />} />
          <Route path="/elbow-fracture-surgery-in-hyderabad/" element={<GolfersElbow />} />
          <Route path="/robotic-knee-replacement-surgery-in-hyderabad" element={<RoboticSurgery />} />
          <Route path="/robotic-knee-replacement-surgery-in-hyderabad/" element={<RoboticSurgery />} />
          <Route path="/category/blog" element={<Blog />} />
          <Route path="/category/blog/" element={<Blog />} />
          <Route path="/total-knee-replacement" element={<KneeReplacement />} />
          <Route path="/total-knee-replacement/" element={<KneeReplacement />} />
          <Route path="/acl-surgery-anterior-cruciate-ligament" element={<ACLReconstruction />} />
          <Route path="/acl-surgery-anterior-cruciate-ligament/" element={<ACLReconstruction />} />
          <Route path="/home/testimonials" element={<TestimonialsPage />} />
          <Route path="/home/testimonials/" element={<TestimonialsPage />} />
          <Route path="/shoulder-hemiarthroplasty" element={<ShoulderReplacement />} />
          <Route path="/shoulder-hemiarthroplasty/" element={<ShoulderReplacement />} />
          <Route path="/anatomic-total-shoulder-arthroplasty" element={<ShoulderReplacement />} />
          <Route path="/anatomic-total-shoulder-arthroplasty/" element={<ShoulderReplacement />} />
          <Route path="/reverse-shoulder-arthroplasty" element={<ShoulderReplacement />} />
          <Route path="/reverse-shoulder-arthroplasty/" element={<ShoulderReplacement />} />
          <Route path="/blog/" element={<Blog />} />
          <Route path="/knee-ligament-injuries" element={<ACLReconstruction />} />
          <Route path="/knee-ligament-injuries/" element={<ACLReconstruction />} />
          <Route path="/faq/" element={<FAQPage />} />
          <Route path="/elbow-arthroscopy" element={<GolfersElbow />} />
          <Route path="/elbow-arthroscopy/" element={<GolfersElbow />} />
          <Route path="/shoulder-reconstruction-surgeon-in-hyderabad" element={<ShoulderArthroscopy />} />
          <Route path="/shoulder-reconstruction-surgeon-in-hyderabad/" element={<ShoulderArthroscopy />} />
          <Route path="/elbow-replacement-surgery-in-hyderabad" element={<GolfersElbow />} />
          <Route path="/elbow-replacement-surgery-in-hyderabad/" element={<GolfersElbow />} />
          <Route path="/rotator-cuff-repair" element={<RotatorCuffTear />} />
          <Route path="/rotator-cuff-repair/" element={<RotatorCuffTear />} />
          <Route path="/fractures-treatment-in-hyderabad" element={<Trauma />} />
          <Route path="/fractures-treatment-in-hyderabad/" element={<Trauma />} />
          <Route path="/shoulder-surgery" element={<ShoulderArthroscopy />} />
          <Route path="/shoulder-surgery/" element={<ShoulderArthroscopy />} />
          <Route path="/elbow-surgery" element={<GolfersElbow />} />
          <Route path="/elbow-surgery/" element={<GolfersElbow />} />
          <Route path="/contact-us" element={<Contact />} />
          <Route path="/contact-us/" element={<Contact />} />
          
          {/* Dynamic condition routes based on site config */}
          {conditions?.map((condition) => (
            <Route 
              key={condition.id}
              path={condition.slug} 
              element={<ConditionPage conditionId={condition.id} />} 
            />
          ))}
          
          {/* Fallback for unknown routes */}
          <Route path="*" element={<DynamicHome />} />
        </Routes>
        </Suspense>
      </main>
      <DynamicFooter />
      <ChatWidget />
      <Toaster />
    </div>
  );
};

// Admin Routes Component
const AdminRoutes = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/" element={
        <ProtectedAdminRoute>
          <AdminLayout />
        </ProtectedAdminRoute>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="subscribers" element={<AdminSubscribers />} />
        <Route path="groups" element={<AdminGroups />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="bulk-broadcast" element={<AdminBulkBroadcast />} />
        <Route path="calendar" element={<AdminCalendar />} />
        <Route path="send" element={<AdminSendMessage />} />
        <Route path="sync" element={<AdminSync />} />
        <Route path="logs" element={<AdminLogs />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <HelmetProvider>
      <SiteProvider>
        <LanguageProvider>
          <AdminAuthProvider>
            <BrowserRouter>
              <Routes>
                {/* Admin Routes */}
                <Route path="/admin/*" element={<AdminRoutes />} />
                
                {/* Main Site Routes */}
                <Route path="/*" element={<AppContent />} />
              </Routes>
              <Toaster />
            </BrowserRouter>
          </AdminAuthProvider>
        </LanguageProvider>
      </SiteProvider>
    </HelmetProvider>
  );
}

export default App;
